/** Player preferences (volumes, screen layout, ...), persisted on the device. */
import { computed, type ReadonlySignal, type Signal, signal } from '@preact/signals';
import { DEFAULT_GAME_SETTINGS, type GameSettings, type LayoutPreference } from '../game/contract';
import { HOLIDAYS, type Holiday, type Season } from '../season/season';
import { type KeyValueStorage, readJson, STORAGE_KEYS, writeJson } from './storage';
import { booleanOr, isOneOf, isRecord, unitInterval } from './validate';

/**
 * Everything the game engine needs plus shell-only settings. The engine's season is not
 * stored: it follows from the date and `festive` (see SettingsStore.season).
 */
export interface ClientSettings extends Omit<GameSettings, 'season'> {
  /** Volume of the other players' voices, 0..1. */
  voiceVolume: number;
  /**
   * «Праздничное оформление», per holiday: its decorations and tunes in its season. Each
   * holiday asks again: Halloween switched off still leaves winter on.
   */
  festive: Readonly<Record<Holiday, boolean>>;
}

export const DEFAULT_CLIENT_SETTINGS: ClientSettings = {
  sfxVolume: DEFAULT_GAME_SETTINGS.sfxVolume,
  musicVolume: DEFAULT_GAME_SETTINGS.musicVolume,
  layout: DEFAULT_GAME_SETTINGS.layout,
  showGhosts: DEFAULT_GAME_SETTINGS.showGhosts,
  haptics: DEFAULT_GAME_SETTINGS.haptics,
  voiceVolume: 1,
  festive: { halloween: true, winter: true },
};

export const LAYOUT_PREFERENCES: readonly LayoutPreference[] = ['auto', 'single', 'split'];

export class SettingsStore {
  private readonly value: Signal<ClientSettings>;
  readonly settings: ReadonlySignal<ClientSettings>;
  /**
   * The holiday season the app shows: the calendar's (or the dev override's), unless the
   * player switched «Праздничное оформление» off for that holiday.
   */
  readonly season: ReadonlySignal<Season>;
  /** The engine's part of the settings (stable identity while those fields do not change). */
  readonly game: ReadonlySignal<GameSettings>;

  /** `calendar`: the season of the date (or the dev override), kept up to date by the app. */
  constructor(
    private readonly storage: KeyValueStorage,
    readonly calendar: ReadonlySignal<Season> = signal<Season>('none'),
  ) {
    this.value = signal(
      readJson(storage, STORAGE_KEYS.settings, parseSettings) ?? { ...DEFAULT_CLIENT_SETTINGS },
    );
    this.settings = this.value;
    this.season = computed(() => {
      const season = calendar.value;
      return season !== 'none' && this.value.value.festive[season] ? season : 'none';
    });
    let lastGame: GameSettings | null = null;
    this.game = computed(() => {
      const { sfxVolume, musicVolume, layout, showGhosts, haptics } = this.value.value;
      const season = this.season.value;
      const next: GameSettings = { sfxVolume, musicVolume, layout, showGhosts, haptics, season };
      if (lastGame && sameGameSettings(lastGame, next)) return lastGame;
      lastGame = next;
      return next;
    });
  }

  /** Applies a change (values are validated and clamped) and persists it. */
  update(patch: Partial<ClientSettings>): void {
    const next = parseSettings({ ...this.value.peek(), ...patch });
    this.value.value = next;
    writeJson(this.storage, STORAGE_KEYS.settings, next);
  }

  /** «Праздничное оформление» of one holiday on or off (the others keep theirs). */
  setFestive(holiday: Holiday, on: boolean): void {
    this.update({ festive: { ...this.value.peek().festive, [holiday]: on } });
  }
}

/** Valid settings from stored data; unknown or broken fields fall back to the defaults. */
export function parseSettings(raw: unknown): ClientSettings {
  const base = DEFAULT_CLIENT_SETTINGS;
  if (!isRecord(raw)) return { ...base };
  return {
    sfxVolume: unitInterval(raw.sfxVolume, base.sfxVolume),
    musicVolume: unitInterval(raw.musicVolume, base.musicVolume),
    voiceVolume: unitInterval(raw.voiceVolume, base.voiceVolume),
    layout: isOneOf(LAYOUT_PREFERENCES, raw.layout) ? raw.layout : base.layout,
    showGhosts: booleanOr(raw.showGhosts, base.showGhosts),
    haptics: booleanOr(raw.haptics, base.haptics),
    festive: parseFestive(raw.festive, base.festive),
  };
}

/** Each holiday's switch; a missing or broken one is on. */
function parseFestive(
  raw: unknown,
  base: Readonly<Record<Holiday, boolean>>,
): Record<Holiday, boolean> {
  const stored: Record<string, unknown> = isRecord(raw) ? raw : {};
  const festive = { ...base };
  for (const holiday of HOLIDAYS) festive[holiday] = booleanOr(stored[holiday], base[holiday]);
  return festive;
}

function sameGameSettings(a: GameSettings, b: GameSettings): boolean {
  return (
    a.sfxVolume === b.sfxVolume &&
    a.musicVolume === b.musicVolume &&
    a.layout === b.layout &&
    a.showGhosts === b.showGhosts &&
    a.haptics === b.haptics &&
    a.season === b.season
  );
}
